import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Search, Eye, CheckCircle, Folder, ChevronsDown, ChevronsUp, ExternalLink, Layers, XCircle } from 'lucide-react';
import BookmarkButton from './BookmarkButton';
import ComponentTypeIcon from './ComponentTypeIcon';
import { Project, Component, ProjectWithComponents, SearchFilters, SortOption } from '../types';
import { filterComponents, sortComponents, getStatusColor, getAccessibilityFeatures, formatDate } from '../utils/filters';

interface UnifiedTableViewProps {
  projects?: ProjectWithComponents[];
  showBookmarksOnly?: boolean;
  bookmarkedComponents?: Component[];
}

// Helper function to check if dev mode is enabled via URL parameter
const checkDevMode = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('dev') === 'true';
  }
  return false;
};

const UnifiedTableView: React.FC<UnifiedTableViewProps> = ({ 
  projects = [], 
  showBookmarksOnly = false,
  bookmarkedComponents = []
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Use bookmarked components if in bookmarks-only mode, otherwise use projects with their components
  const effectiveProjects = useMemo(() => {
    if (showBookmarksOnly) {
      if (checkDevMode()) {
        console.log('UnifiedTableView: Creating virtual projects from bookmarked components:', bookmarkedComponents.length);
        console.log('UnifiedTableView: Sample bookmarked components:', bookmarkedComponents.slice(0, 3).map(c => ({
          name: c.name,
          projectName: c.projectName,
          projectId: c.projectId
        })));
      }

      // In bookmarks mode, create virtual projects from bookmarked components
      console.log('UnifiedTableView: Processing bookmarked components:', bookmarkedComponents.map(c => ({
        name: c.name,
        projectId: c.projectId,
        projectName: c.projectName,
        status: c.status
      })));
      
      const projectGroups = bookmarkedComponents.reduce((groups, component) => {
        // Use projectId as the primary key for grouping, fallback to projectName
        const projectKey = component.projectId || component.projectName;
        const projectName = component.projectName;
        
        // Skip components without project information
        if (!projectKey || !projectName) {
          console.warn(`UnifiedTableView: Skipping component ${component.name} without project info`);
          console.warn(`UnifiedTableView: Component data:`, { name: component.name, projectId: component.projectId, projectName: component.projectName });
          return groups;
        }
        
        console.log(`UnifiedTableView: Grouping component ${component.name} under project key: ${projectKey}, name: ${projectName}`);
        
        if (!groups[projectKey]) {
          // Get project metadata from the first component in this group
          const firstComponent = bookmarkedComponents.find(c => 
            (c.projectId || c.projectName) === projectKey
          );
          const projectMetadata = (firstComponent as any)?._projectMetadata;
          
          console.log(`UnifiedTableView: Processing project ${projectKey}:`, {
            firstComponent: firstComponent ? {
              name: firstComponent.name,
              projectId: firstComponent.projectId,
              projectName: firstComponent.projectName,
              hasMetadata: !!(firstComponent as any)._projectMetadata
            } : 'No component found',
            projectMetadata: projectMetadata ? {
              id: projectMetadata.id,
              name: projectMetadata.name,
              lastUpdated: projectMetadata.lastUpdated,
              repository: projectMetadata.repository
            } : 'No metadata'
          });
          
          // ONLY create projects with API metadata - no fallbacks
          if (!projectMetadata) {
            console.warn(`UnifiedTableView: Skipping project ${projectKey} - no API metadata available`);
            return groups;
          }
          
          console.log(`UnifiedTableView: Creating virtual project with API metadata:`, {
            projectKey,
            projectName,
            metadata: {
              description: projectMetadata.description,
              status: projectMetadata.status,
              lastUpdated: projectMetadata.lastUpdated,
              repository: projectMetadata.repository,
              patternlabUrl: projectMetadata.patternlabUrl
            }
          });
          
          groups[projectKey] = {
            id: projectKey,
            name: projectName,
            description: projectMetadata.description,
            components: [],
            status: projectMetadata.status,
            lastUpdated: projectMetadata.lastUpdated,
            repository: projectMetadata.repository,
            patternlabUrl: projectMetadata.patternlabUrl
          };
        }
        groups[projectKey].components.push(component);
        return groups;
      }, {} as Record<string, ProjectWithComponents>);

      const result = Object.values(projectGroups);
      
      console.log('UnifiedTableView: Created virtual projects:', result.map(p => ({
        id: p.id,
        name: p.name,
        componentCount: p.components.length
      })));

      return result;
    }
    return projects;
  }, [showBookmarksOnly, bookmarkedComponents, projects]);

  // Ensure projects is always an array
  const safeProjects = useMemo(() => {
    return Array.isArray(effectiveProjects) ? effectiveProjects : [];
  }, [effectiveProjects]);

  // Get all components from all projects for filtering purposes
  const allComponents = useMemo(() => {
    return safeProjects.flatMap(project => project.components);
  }, [safeProjects]);

  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    type: [],
    status: '',
    tags: [],
    cms: [],
    themes: [],
    accessibility: []
  });
  const [sortOption, setSortOption] = useState<SortOption>({
    field: 'name',
    direction: 'asc'
  });
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [expandedVariants, setExpandedVariants] = useState<Set<string>>(new Set());
  const [isFilterSticky, setIsFilterSticky] = useState(false);
  const filterRef = React.useRef<HTMLDivElement>(null);
  const [filterHeight, setFilterHeight] = useState(0);

  // Auto-expand all projects when data loads or when switching modes
  useEffect(() => {
    if (safeProjects.length > 0) {
      const projectNames = safeProjects.map(p => p.name);
      setExpandedProjects(new Set(projectNames));
    }
  }, [safeProjects.length, showBookmarksOnly]); // Only depend on length and mode, not the entire array

  // Handle scroll for sticky filters and measure filter height
  useEffect(() => {
    const handleScroll = () => {
      if (filterRef.current) {
        const rect = filterRef.current.getBoundingClientRect();
        const headerHeight = 64; // Header height
        
        // Check if filter is sticky (for visual feedback only)
        const isCurrentlySticky = rect.top <= headerHeight;
        setIsFilterSticky(isCurrentlySticky);
        
        // Measure filter height if not already set
        if (filterHeight === 0) {
          setFilterHeight(filterRef.current.offsetHeight);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Call once on mount to measure height
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [filterHeight]);

  // Get available types from all components
  const availableTypes = useMemo(() => {
    const values = new Set<string>();
    const typeMapping = new Map<string, string[]>(); // Clean name -> original names
    
    allComponents.forEach(component => {
      const value = component.type || 'unknown';
      if (value) {
        // Remove numeric prefixes like "10-", "20-", etc.
        const cleanType = value.replace(/^\d+-/, '');
        values.add(cleanType);
        
        // Map clean name to original names for filtering
        if (!typeMapping.has(cleanType)) {
          typeMapping.set(cleanType, []);
        }
        typeMapping.get(cleanType)!.push(value);
      }
    });
    
    return { cleanTypes: Array.from(values).sort(), typeMapping };
  }, [allComponents]);

  // Initialize filters with all available types
  useEffect(() => {
    if (availableTypes.cleanTypes.length > 0 && filters.type.length === 0) {
      // Initialize with all original type values
      const allOriginalTypes = availableTypes.cleanTypes.flatMap(cleanType => 
        availableTypes.typeMapping.get(cleanType) || []
      );
      setFilters(prev => ({ ...prev, type: allOriginalTypes }));
    }
  }, [availableTypes, filters.type.length]);

  // Create a unified interface for projects with components
  interface ProjectWithComponentsDisplay {
    id: string;
    name: string;
    components: (Component | { type: string; isSeparator: true })[];
    isExpanded: boolean;
    status?: string;
    lastUpdated?: string;
    repository?: string;
    patternlabUrl?: string;
  }

  // Process projects with their components
  const projectsWithComponents = useMemo((): ProjectWithComponentsDisplay[] => {
    return safeProjects
      .sort((a, b) => a.name.localeCompare(b.name)) // Sort projects A-Z
      .map(project => {
        return {
          id: project.id,
          name: project.name,
          components: project.components,
          isExpanded: expandedProjects.has(project.name),
          status: project.status,
          lastUpdated: project.lastUpdated,
          repository: project.repository,
          patternlabUrl: project.patternlabUrl
        };
      });
  }, [safeProjects, expandedProjects]);

  // Filter and sort components with subgrouping by type
  const filteredProjectsWithComponents = useMemo(() => {
    return projectsWithComponents.map(project => {
      // Filter out separator objects and only pass actual components to filterComponents
      const actualComponents = project.components.filter((item): item is Component => 
        !('isSeparator' in item)
      );
      
      const filteredComponents = filterComponents(actualComponents, { ...filters, query: searchQuery });
      
      // Group components by type and sort each group
      const groupedComponents = filteredComponents.reduce((groups, component) => {
        const type = component.type || 'unknown';
        if (!groups[type]) {
          groups[type] = [];
        }
        groups[type].push(component);
        return groups;
      }, {} as Record<string, Component[]>);

      // Sort components within each type group
      Object.keys(groupedComponents).forEach(type => {
        groupedComponents[type] = sortComponents(groupedComponents[type], sortOption);
      });

      // Sort type groups alphabetically
      const sortedTypes = Object.keys(groupedComponents).sort();
      
      // Flatten back to array with type separators
      const sortedComponents: (Component | { type: string; isSeparator: true })[] = [];
      sortedTypes.forEach(type => {
        sortedComponents.push({ type, isSeparator: true });
        sortedComponents.push(...groupedComponents[type]);
      });

      return {
        ...project,
        components: sortedComponents
      };
    }).filter(project => 
      project.components.length > 0 || 
      project.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [projectsWithComponents, filters, searchQuery, sortOption]);

  const toggleProjectExpansion = (projectName: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectName)) {
      newExpanded.delete(projectName);
    } else {
      newExpanded.add(projectName);
    }
    setExpandedProjects(newExpanded);
  };

  const toggleVariantsExpansion = (componentName: string) => {
    const newExpanded = new Set(expandedVariants);
    if (newExpanded.has(componentName)) {
      newExpanded.delete(componentName);
    } else {
      newExpanded.add(componentName);
    }
    setExpandedVariants(newExpanded);
  };

  const toggleAllProjects = () => {
    if (expandedProjects.size === safeProjects.length) {
      // All projects are expanded, so collapse all
      setExpandedProjects(new Set());
    } else {
      // Some or no projects are expanded, so expand all
      setExpandedProjects(new Set(safeProjects.map(p => p.name)));
    }
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      query: '',
      type: availableTypes.cleanTypes.flatMap(cleanType => 
        availableTypes.typeMapping.get(cleanType) || []
      ),
      status: '',
      tags: [],
      cms: [],
      themes: [],
      accessibility: []
    });
    setSearchQuery('');
  };

  const getUniqueValues = (field: keyof Component): string[] => {
    const values = new Set<string>();
    allComponents.forEach(component => {
      const value = component[field];
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (typeof v === 'string') {
            values.add(v);
          } else if (typeof v === 'object' && v !== null) {
            // Handle nested objects like { name: string; file: string }
            if ('name' in v && typeof v.name === 'string') {
              values.add(v.name);
            } else if ('theme' in v && typeof v.theme === 'string') {
              values.add(v.theme);
            }
          }
        });
      } else if (value) {
        values.add(value as string);
      }
    });
    return Array.from(values).sort();
  };

  const availableStatuses = getUniqueValues('status');
  const availableTags = Array.from(new Set(allComponents.flatMap(c => c.tags))).sort();



  return (
    <>
      {/* Sticky Filter Bar */}
      <div 
        ref={filterRef}
        className="sticky top-20 z-10 nav-glass transition-all duration-300 mb-6 rounded-2xl shadow-lg"
      >
        <div className="p-4">
          {/* Header Row */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 mb-4">
            <div className="flex items-center space-x-4">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                {showBookmarksOnly ? 'Bookmarked Components' : 'PatternLab Components'} ({allComponents.length})
              </h2>
              
              <button
                onClick={toggleAllProjects}
                className="btn-glass text-sm px-4 py-2 flex items-center space-x-2"
              >
                {expandedProjects.size === safeProjects.length ? (
                  <>
                    <ChevronsUp className="w-4 h-4" />
                    <span>Collapse All</span>
                  </>
                ) : (
                  <>
                    <ChevronsDown className="w-4 h-4" />
                    <span>Expand All</span>
                  </>
                )}
              </button>
            </div>

            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search projects and components..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-glass pl-10 pr-4"
                />
              </div>

              {/* Sort Dropdown */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Sort:</label>
                <select
                  value={`${sortOption.field}-${sortOption.direction}`}
                  onChange={(e) => {
                    const [field, direction] = e.target.value.split('-');
                    setSortOption({ field: field as any, direction: direction as any });
                  }}
                  className="input-glass text-sm"
                >
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="lastUpdated-desc">Recently Updated</option>
                  <option value="lastUpdated-asc">Oldest First</option>
                  <option value="type-asc">Type A-Z</option>
                  <option value="status-asc">Status A-Z</option>
                </select>
              </div>
            </div>
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Type Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Type:</label>
              <div className="flex flex-wrap gap-2">
                {availableTypes.cleanTypes.map(cleanType => {
                  const isActive = filters.type.some(type => availableTypes.typeMapping.get(cleanType)?.includes(type));
                  return (
                    <button
                      key={cleanType}
                      onClick={() => {
                        const originalTypes = availableTypes.typeMapping.get(cleanType) || [];
                        if (isActive) {
                          handleFilterChange('type', filters.type.filter(t => !originalTypes.includes(t)));
                        } else {
                          handleFilterChange('type', [...filters.type, ...originalTypes]);
                        }
                      }}
                      className={`px-3 py-2 text-sm font-bold rounded-xl transition-all duration-200 border flex items-center space-x-2 ${
                        isActive
                          ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 border-slate-800 dark:border-slate-200 shadow-md hover:bg-slate-900 dark:hover:bg-slate-100 hover:border-slate-900 dark:hover:border-slate-100'
                          : 'bg-white/60 dark:bg-slate-700/60 text-slate-900 dark:text-white border-slate-300 dark:border-slate-600 hover:bg-white/80 dark:hover:bg-slate-700/80 hover:border-slate-400 dark:hover:border-slate-500 shadow-sm backdrop-blur-sm'
                      } capitalize`}
                    >
                      <ComponentTypeIcon type={cleanType} size="sm" />
                      <span>{cleanType}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Status:</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="input-glass text-sm"
              >
                <option value="">All Status</option>
                {availableStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {/* Tags Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Tags:</label>
              <select
                value={filters.tags[0] || ''}
                onChange={(e) => handleFilterChange('tags', e.target.value ? [e.target.value] : [])}
                className="input-glass text-sm"
              >
                <option value="">All Tags</option>
                {availableTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            <button
              onClick={clearFilters}
              className="btn-glass text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>



      {/* Table Container */}
      <div className="bg-white/40 dark:bg-slate-800/40 backdrop-blur-xl rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50 overflow-hidden">
        {/* Unified Table */}
        <div className="table-glass">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead className="bg-slate-50/60 dark:bg-slate-700/60 backdrop-blur-sm">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Project / Component
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Tags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Accessibility
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white/40 dark:bg-slate-800/40 divide-y divide-slate-200 dark:divide-slate-700">
              {filteredProjectsWithComponents.map(project => (
                <React.Fragment key={project.name}>
                  {/* Project Row */}
                  <tr 
                    className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProjectExpansion(project.name);
                          }}
                          className="mr-2 text-gray-400 hover:text-gray-600 w-6 h-6 flex items-center justify-center"
                        >
                          {project.isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                        <Folder className="w-5 h-5 text-primary-600 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {project.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {project.name} • {project.components.filter(item => !('isSeparator' in item)).length} components
                          </div>
                        </div>
                        <div className="ml-4 flex items-center space-x-2">
                          {project.repository && (
                            <a
                              href={project.repository}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              title="Repository"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge ${getStatusColor(project.status || 'active')}`}>
                        {project.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(project.lastUpdated)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    </td>
                  </tr>

                  {/* Component Rows */}
                  {project.isExpanded && project.components.map((item, index) => {
                    // Check if this is a type separator
                    if ('isSeparator' in item && item.isSeparator) {
                      return (
                        <tr key={`separator-${item.type}-${index}`} className="bg-gray-100 dark:bg-gray-600">
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div className="flex items-center pl-8 space-x-3">
                              <ComponentTypeIcon type={item.type} size="sm" />
                              <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                {item.type.replace(/^\d+-/, '')}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          </td>
                        </tr>
                      );
                    }

                    // Regular component row
                    const component = item as Component;
                    return (
                      <React.Fragment key={component.name}>
                        <tr 
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          onClick={() => navigate(`/projects/${project.id}/components/${component.name}`)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center pl-12">
                              <div onClick={(e) => e.stopPropagation()}>
                                <BookmarkButton 
                                  component={component} 
                                  project={{
                                    id: project.id,
                                    name: project.name,
                                    description: '',
                                    owner: [],
                                    maintainers: [],
                                    status: 'active',
                                    techStack: { templating: 'unknown', css: 'unknown', js: 'unknown', libraries: [] },
                                    cms: [],
                                    themes: [],
                                    testing: { visual: 'unknown', accessibility: 'unknown' },
                                    componentCount: 0,
                                    patternlabVersion: 'unknown',
                                    lastUpdated: new Date().toISOString(),
                                    repository: ''
                                  }} 
                                  size="sm" 
                                />
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {component.name}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {component.name}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`badge ${getStatusColor(component.status || 'draft')}`}>
                          {component.status || 'draft'}
                        </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-wrap gap-1">
                                                          {component.tags && component.tags.length > 0 ? (
                              component.tags.map((tag: string) => (
                                  <span
                                    key={tag}
                                    className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded"
                                  >
                                    {tag}
                                  </span>
                                ))
                              ) : (
                                <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-wrap gap-1">
                              {getAccessibilityFeatures(component).map(({ feature, value }) => (
                                <span
                                  key={feature}
                                  className={`text-xs px-2 py-1 rounded flex items-center ${
                                    value 
                                      ? 'bg-success-100 text-success-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {value ? (
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                  ) : (
                                    <XCircle className="w-3 h-3 mr-1" />
                                  )}
                                  {feature}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(component.lastUpdated || project.lastUpdated)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const projectPatternlabUrl = project.patternlabUrl || '';
                                const componentPreviewUrl = component.previewUrl || '';
                                
                                let previewUrl = '#';
                                if (componentPreviewUrl.startsWith('http')) {
                                  // If component.previewUrl is already a full URL, use it directly
                                  previewUrl = componentPreviewUrl;
                                } else if (projectPatternlabUrl && componentPreviewUrl) {
                                  // Combine project.patternlabUrl + component.previewUrl
                                  const baseUrl = projectPatternlabUrl.replace(/\/$/, ''); // Remove trailing slash
                                  const previewPath = componentPreviewUrl.replace(/^\//, ''); // Remove leading slash
                                  previewUrl = `${baseUrl}/${previewPath}`;
                                } else if (projectPatternlabUrl) {
                                  // If component.previewUrl is empty, use project.patternlabUrl only
                                  previewUrl = projectPatternlabUrl;
                                }
                                
                                if (previewUrl !== '#') {
                                  window.open(previewUrl, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 shadow-sm hover:shadow-md"
                              disabled={!component.previewUrl && !project.patternlabUrl}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </button>
                          </td>
                        </tr>
                        
                        {/* Variants Row */}
                        {component.variants && component.variants.length > 0 && (
                          <tr 
                            className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                            onClick={() => toggleVariantsExpansion(component.name)}
                          >
                            <td className="px-6 py-3 whitespace-nowrap">
                              <div className="flex items-center pl-20">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleVariantsExpansion(component.name);
                                  }}
                                  className="mr-2 text-gray-400 hover:text-gray-600"
                                >
                                  {expandedVariants.has(component.name) ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </button>
                                <Layers className="w-4 h-4 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">
                                  Show {component.variants.length} variant{component.variants.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                            </td>
                          </tr>
                        )}

                        {/* Variant Detail Rows */}
                        {component.variants && component.variants.length > 0 && expandedVariants.has(component.name) && (
                          <>
                            {component.variants.map((variant, index) => (
                                                             <tr 
                                 key={`${component.name}-variant-${typeof variant === 'string' ? variant : variant.name}-${index}`} 
                                 className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer bg-gray-50 dark:bg-gray-700"
                                 onClick={() => navigate(`/projects/${project.id}/components/${component.name}?variant=${typeof variant === 'string' ? variant : variant.name}`)}
                               >
                                <td className="px-6 py-3 whitespace-nowrap">
                                  <div className="flex items-center pl-24">
                                    <Layers className="w-3 h-3 text-gray-400 mr-2" />
                                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                      {typeof variant === 'string' ? variant : variant.name}
                                    </div>
                                  </div>
                                </td>

                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {formatDate(component.lastUpdated || project.lastUpdated)}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const projectPatternlabUrl = project.patternlabUrl || '';
                                      const variantPreviewUrl = typeof variant === 'object' && variant.previewUrl ? variant.previewUrl : '';
                                      
                                      let previewUrl = '#';
                                      if (variantPreviewUrl.startsWith('http')) {
                                        // If variant.previewUrl is already a full URL, use it directly
                                        previewUrl = variantPreviewUrl;
                                      } else if (projectPatternlabUrl && variantPreviewUrl) {
                                        // Combine project.patternlabUrl + variant.previewUrl
                                        const baseUrl = projectPatternlabUrl.replace(/\/$/, ''); // Remove trailing slash
                                        const previewPath = variantPreviewUrl.replace(/^\//, ''); // Remove leading slash
                                        previewUrl = `${baseUrl}/${previewPath}`;
                                      } else if (projectPatternlabUrl) {
                                        // If variant.previewUrl is empty, use project.patternlabUrl only
                                        previewUrl = projectPatternlabUrl;
                                      }
                                      
                                      if (previewUrl !== '#') {
                                        window.open(previewUrl, '_blank', 'noopener,noreferrer');
                                      }
                                    }}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 shadow-sm hover:shadow-md"
                                    disabled={!(typeof variant === 'object' && variant.previewUrl) && !project.patternlabUrl}
                                  >
                                    <Eye className="w-3 h-3 mr-1" />
                                    Preview
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredProjectsWithComponents.length === 0 && (
          <div className="text-center py-12">
            <Search className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No projects or components found
            </h3>
            <p className="text-gray-600">
              Try adjusting your search terms or filters
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default UnifiedTableView;
